/**
 * Parses agent message strings for Help Center content blocks.
 *
 * Supported block formats:
 *   [HELP_CENTER_CARD:{...json...}]
 *   [HELP_CENTER_PAGE:{...json...}]
 *   [HELP_CENTER_MEDIA:{...json...}]
 */
import { parseProductBlocks, ProductCardData, CheckoutLinkData } from './productBlocks';
import { parseOrderSegments, OrderCardData } from './orderBlocks';
import type { LookCardData } from './lookBlocks';

export interface HelpCenterCardData {
  article_id: string;
  title: string;
  description: string;
  slug: string;
  url?: string;
  thumbnail?: string;
}

export interface HelpCenterPageData {
  article_id: string;
  title: string;
  slug: string;
  url?: string;
}

export interface HelpCenterMediaData {
  type: 'image' | 'video' | 'youtube';
  url: string;
  alt?: string;
  caption?: string;
}

export type HelpCenterSegment =
  | { type: 'text'; content: string }
  | { type: 'card'; data: HelpCenterCardData }
  | { type: 'page'; data: HelpCenterPageData }
  | { type: 'media'; data: HelpCenterMediaData };

const BLOCK_RE = /\[HELP_CENTER_(CARD|PAGE|MEDIA):(\{[^[\]]*\})\]/g;

export function parseHelpCenterBlocks(message: string): HelpCenterSegment[] {
  const segments: HelpCenterSegment[] = [];
  let lastIndex = 0;

  for (const match of message.matchAll(BLOCK_RE)) {
    const start = match.index!;
    // Push preceding text
    if (start > lastIndex) {
      segments.push({ type: 'text', content: message.slice(lastIndex, start) });
    }

    const blockType = match[1].toLowerCase() as 'card' | 'page' | 'media';
    try {
      const data = JSON.parse(match[2]);
      if (blockType === 'card' || blockType === 'page') {
        const rawSlug = typeof data.slug === 'string' ? data.slug.trim() : '';
        if (!rawSlug && typeof data.url === 'string' && data.url) {
          const fromUrl = data.url.split('?')[0].split('#')[0].split('/').filter(Boolean).pop();
          if (fromUrl) data.slug = fromUrl;
        } else {
          data.slug = rawSlug;
        }
      }
      segments.push({ type: blockType, data });
    } catch {
      segments.push({ type: 'text', content: match[0] });
    }

    lastIndex = start + match[0].length;
  }

  // Trailing text
  if (lastIndex < message.length) {
    segments.push({ type: 'text', content: message.slice(lastIndex) });
  }

  // If no blocks found, return the whole message as a single text segment
  if (segments.length === 0) {
    return [{ type: 'text', content: message }];
  }

  return segments;
}

/**
 * Voice-mode side-dock segments — composes `parseHelpCenterBlocks`,
 * `parseOrderSegments` and `parseProductBlocks` so help-center cards
 * (article/page/media), Shopify order cards AND product cards can all
 * land in the same dock without coupling any parser to another's
 * marker family.
 *
 * `checkout_link` segments from `parseProductBlocks` are passed through
 * (2026-06-29) so the voice panel can render an actionable checkout
 * button instead of the agent dictating a raw URL aloud. See
 * files/widget/Backlog-ClickableLinks.md.
 */
export type VoiceDockSegment =
  | { type: 'card'; data: HelpCenterCardData }
  | { type: 'page'; data: HelpCenterPageData }
  | { type: 'media'; data: HelpCenterMediaData }
  | { type: 'product_card'; data: ProductCardData }
  | { type: 'checkout_link'; data: CheckoutLinkData }
  | { type: 'order_card'; data: OrderCardData }
  | { type: 'look_card'; data: LookCardData };

export function parseVoiceDockBlocks(message: string): VoiceDockSegment[] {
  const hc = parseHelpCenterBlocks(message);
  const out: VoiceDockSegment[] = [];
  for (const seg of hc) {
    if (seg.type !== 'text') {
      out.push(seg as VoiceDockSegment);
      continue;
    }
    // Within a help-center text run, extract ORDER_CARD first — its
    // brace-balanced parser (findOrderCardMarkers) safely owns the nested
    // `line_items:[{...}]` JSON, which a flat regex would mangle. Then feed
    // the remaining text sub-segments through the product-card parser. The
    // two marker families ([ORDER_CARD:…] vs [PRODUCT_CARD:…]) are disjoint,
    // so neither swallows the other.
    const orderSegs = parseOrderSegments(seg.content);
    for (const o of orderSegs) {
      if (o.type === 'order_card') {
        out.push({ type: 'order_card', data: o.data });
        continue;
      }
      const ps = parseProductBlocks(o.content);
      for (const p of ps) {
        if (p.type === 'product_card') {
          out.push({ type: 'product_card', data: p.data });
        } else if (p.type === 'checkout_link') {
          out.push({ type: 'checkout_link', data: p.data });
        }
      }
    }
  }
  return out;
}

/**
 * Plan-VoiceServerSideCards (2026-06-29) — voice cards are now pushed by the
 * backend out-of-band over the voice WS (the Gemini Live model no longer
 * emits markers in its spoken reply, which it would otherwise read aloud).
 *
 * The backend sends `{"type":"voice_cards","cards":[{"kind","data"},…]}` where
 * each `data` is ALREADY the exact shape the matching card component renders
 * (the same shapes `parseVoiceDockBlocks` produces). So this is a pure
 * kind→segment remap with no re-parsing. Unknown kinds are dropped so a
 * future backend card type can't crash an older FE bundle.
 */
export interface VoiceCard {
  kind: string;
  data: unknown;
}

export function voiceCardsToSegments(cards: VoiceCard[]): VoiceDockSegment[] {
  const out: VoiceDockSegment[] = [];
  if (!Array.isArray(cards)) return out;
  for (const card of cards) {
    if (!card || typeof card !== 'object' || !card.data) continue;
    switch (card.kind) {
      case 'product':
        out.push({ type: 'product_card', data: card.data as ProductCardData });
        break;
      case 'order':
        out.push({ type: 'order_card', data: card.data as OrderCardData });
        break;
      case 'look':
        out.push({ type: 'look_card', data: card.data as LookCardData });
        break;
      case 'help_center_card':
        // Plan-VoiceRobustArticleCards (2026-07-06) — article cards now arrive
        // server-built via `send_help_center_article` instead of relying on
        // the model leaking a [HELP_CENTER_CARD:…] marker into its transcript.
        // `data` keys are exactly HelpCenterCard's props, and this is the SAME
        // `{type:'card'}` segment the transcript-parse path produces, so
        // VoiceContentPanel renders it with zero render-component change.
        out.push({ type: 'card', data: card.data as HelpCenterCardData });
        break;
      case 'media':
        // NOTE (verified against shipped back/ `2eedc8b`): the backend does
        // NOT emit a `media` kind via voice_cards. KB video still reaches the
        // voice panel through the EXISTING server-appended
        // `[HELP_CENTER_MEDIA:{type,url,alt}]` transcript marker (parsed by
        // `parseVoiceDockBlocks` in the `message.parts` path). This branch is
        // kept only as forward-compat — today it never fires.
        out.push({ type: 'media', data: card.data as HelpCenterMediaData });
        break;
      case 'checkout_link':
        out.push({ type: 'checkout_link', data: card.data as CheckoutLinkData });
        break;
      default:
        // Unknown kind — ignore (forward-compat).
        break;
    }
  }
  return out;
}

/**
 * Stable de-dup key for a voice dock segment, so repeated `voice_cards`
 * pushes within a session (or a model that re-recommends the same product)
 * don't double-render. Keyed by the natural identity of each card family.
 */
export function voiceSegmentKey(seg: VoiceDockSegment): string {
  switch (seg.type) {
    case 'product_card':
      return `product:${seg.data.product_id}`;
    case 'order_card':
      return `order:${seg.data.order_id}`;
    case 'checkout_link':
      return `checkout:${seg.data.url}`;
    case 'media':
      return `media:${seg.data.url}`;
    case 'look_card':
      // Looks carry no id; key on title + the product ids in their slots.
      return `look:${seg.data.title}:${(seg.data.slots || []).map(s => s.product?.product_id).join(',')}`;
    case 'card':
      // Slug FIRST (article_id fallback): during the marker→tool migration a
      // model-leaked [HELP_CENTER_CARD:…] transcript block may omit or
      // fabricate article_id while the server-built voice_cards frame carries
      // the real one — keying on article_id would give the two copies
      // different keys and double-render. Slug is the model-visible stable id
      // (the transcript parser even backfills it from `url`), so both copies
      // collide and mergeVoiceSegments upserts them into ONE card.
      return `card:${seg.data.slug || seg.data.article_id}`;
    case 'page':
      return `page:${seg.data.article_id || seg.data.slug}`;
    default:
      return JSON.stringify(seg);
  }
}

/**
 * Merge a freshly-arrived batch of voice dock segments into the existing
 * session list with UPSERT semantics.
 *
 * Why not the old "dedup against history" approach: keying the seen-set off
 * the WHOLE accumulated array meant any product_id shown earlier in the
 * session was silently dropped when the agent re-sent it ("send it again"),
 * so nothing re-rendered. Here the dedup is PER-BATCH only — a re-sent card
 * is never globally suppressed.
 *
 * Behaviour:
 *  - de-dup WITHIN the incoming batch (keep first of each key);
 *  - drop any existing entries whose key collides with the batch, then append
 *    the batch — so a re-sent card re-surfaces without piling a duplicate tile;
 *  - non-colliding existing entries are retained, so multiple `voice_cards`
 *    messages within ONE turn (e.g. products then an order) all stay visible
 *    (this is an upsert, NOT a blanket replace).
 */
export function mergeVoiceSegments(
  prev: VoiceDockSegment[],
  incoming: VoiceDockSegment[],
): VoiceDockSegment[] {
  const batchSeen = new Set<string>();
  const batch = incoming.filter(s => {
    const k = voiceSegmentKey(s);
    if (batchSeen.has(k)) return false;
    batchSeen.add(k);
    return true;
  });
  if (batch.length === 0) return prev;
  const retained = prev.filter(s => !batchSeen.has(voiceSegmentKey(s)));
  return [...retained, ...batch];
}
